import React, { FC, useEffect, useState } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import { NavLink, useNavigate } from "react-router-dom";
import {
  capitalizeString,
  capitalizeFullString,
  formatBookingUserPhone,
  getTotalPrice,
  mapTimeZone,
} from "../../../utils/common";
import { DateTime } from "luxon";
import { Rating } from "react-simple-star-rating";
import Pagination from "../../../shared/pagination/pagination";
import { useTranslation } from "react-i18next";
import StatusBadge from "../../../shared/components/StatusBadge";
import BookingListLoader from "../../../shared/content-loaders/BookingListLoader";
import { useAppSelector } from "../../../app/hooks";
import { fetchBookingStatus } from "../bookingSlice";

interface BookingListProps {
  bookings: any;
  totalCount: number;
  setSkip: any;
  type: string;
}

const getTeesheetDateTimeISO = (teesheet: any) =>
  teesheet?.teesheetObj?.dateTimeISO ??
  teesheet?.teesheetIdDetail?.dateTimeISO ??
  teesheet?.dateTimeISO;

const groupTeesheetsByClub = (teesheets: any[] = []) => {
  const groups: { club: any; teesheets: any[] }[] = [];
  const indexByClubId = new Map<string, number>();
  teesheets.forEach((ts) => {
    const clubId = String(ts?.club?._id ?? ts?.clubId ?? "");
    const idx = indexByClubId.get(clubId);
    if (idx !== undefined) {
      groups[idx].teesheets.push(ts);
    } else {
      indexByClubId.set(clubId, groups.length);
      groups.push({ club: ts.club, teesheets: [ts] });
    }
  });
  return groups;
};

const formatClubAddressLine = (club: any) => {
  if (!club) return "";
  const city = club.city ? `${club.city}, ` : "";
  const line = `${club.addressOne ?? ""}, ${city}${club.countryName ?? ""}`
    .replace(/^,\s*/, "")
    .trim();
  if (line) return line;
  return club.formattedName ?? (typeof club.address === "string" ? club.address : "");
};

const getBookingUserDisplayName = (user: any, t: (key: string) => string) => {
  if (!user) return t("Bookings.GuestUser");
  if (user?.firstname?.length > 1) {
    return capitalizeString(`${user.firstname} ${user.lastname || ""}`);
  }
  return capitalizeString(`${user.name || t("Bookings.GuestUser")}`);
};

const BookingList: FC<BookingListProps> = ({
  bookings,
  totalCount,
  setSkip,
  type,
}) => {
  const [pageCount, setPageCount] = useState(0);
  const { t } = useTranslation("");
  const navigate = useNavigate();
  const fetchStatus = useAppSelector(fetchBookingStatus);
  const isLoading = fetchStatus === "pending";
  const showEmptyState = fetchStatus === "success" && (!bookings || bookings.length === 0);

  useEffect(() => {
    setPageCount(Math.ceil(totalCount / 10));
  }, [bookings]);

  const openBookingDetail = (bookingId: string) => {
    if (bookingId) navigate(`/booking-detail/${bookingId}`);
  };

  const stopCardNavigation = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.stopPropagation();
  };

  return (
    <>
      {isLoading && <BookingListLoader count={3} />}

      {!isLoading && (
      <div className="bookingsListWrapper">
        {bookings &&
          bookings.length > 0 &&
          bookings.map((booking: any, index: number) => {
            const formattedPhone = formatBookingUserPhone(booking?.user);
            const hasPendingTee = booking?.teesheets?.find(
              (tee: any) => tee?.status?.value === "courseConfirmationPending"
            );

            const bookingDetailPath = `/booking-detail/${booking._id}`;

            return (
              <article
                className="bookingDataBlock bookingListCard bookingListCard--clickable"
                key={booking._id ?? index}
                role="link"
                tabIndex={0}
                onClick={() => openBookingDetail(booking._id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    openBookingDetail(booking._id);
                  }
                }}
                aria-label={`${t("Bookings.ViewDetail")} — ${getBookingUserDisplayName(booking?.user, t)}`}
              >
                <div className="bookingListCard__layout">
                  <div className="bookingListCard__userCol">
                    <span className="userImage">
                      <img
                        src={
                          booking?.user?.profileImage?.path ||
                          "../../assets/images/default-user.png"
                        }
                        alt="user"
                        className="img-fluid"
                      />
                    </span>
                    <div className="bookingListCard__userBody">
                      <h2 className="bookingListCard__name">
                        {getBookingUserDisplayName(booking?.user, t)}
                      </h2>
                      {booking?.user && (
                        <div className="bookingListCard__contact">
                          {formattedPhone && (
                            <span className="bookingListCard__contactItem">
                              <i className="fas fa-phone" aria-hidden="true"></i>
                              {formattedPhone}
                            </span>
                          )}
                          {booking.user?.email && (
                            <span className="bookingListCard__contactItem">
                              <i className="fas fa-envelope" aria-hidden="true"></i>
                              {booking.user.email}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="bookingListCard__meta">
                        <div className="bookingListCard__metaItem">
                          <span className="bookingListCard__metaLabel">{t("Bookings.BookingCode")}</span>
                          <span className="bookingListCard__metaValue bookingListCard__metaValue--code">
                            {booking?.bookingId ?? "—"}
                          </span>
                        </div>
                        <div className="bookingListCard__metaItem">
                          <span className="bookingListCard__metaLabel">{t("Bookings.BookedOn")}</span>
                          <span className="bookingListCard__metaValue">
                            {booking?.createdAt
                              ? DateTime.fromMillis(booking.createdAt, {
                                  zone: mapTimeZone(booking?.teesheets?.[0]?.club),
                                }).toFormat("h:mm a, EEE dd MMM yyyy")
                              : "n/a"}
                          </span>
                        </div>
                        <div className="bookingListCard__metaItem">
                          <span className="bookingListCard__metaLabel">{t("Bookings.TotalPayment")}</span>
                          <span className="bookingListCard__metaValue bookingListCard__metaValue--price">
                            ${getTotalPrice(booking) ?? "0.00"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bookingListCard__courseCol colSeprator">
                    {booking?.bookingType === 4 ? (
                      <>
                        <h3 className="bookingListCard__clubName">
                          {capitalizeFullString(booking?.golfPackage?.name ?? "")}
                        </h3>
                        {booking.teesheets?.length > 0 &&
                          booking.teesheets.map((teesheet: any, teeIndex: number) => (
                            <p key={teeIndex} className="bookingListCard__teeTime">
                              {teesheet?.isNetRate
                                ? `${DateTime.fromISO(getTeesheetDateTimeISO(teesheet), {
                                    zone: mapTimeZone(teesheet?.club),
                                  }).toFormat("dd MMM yyyy")}, ${teesheet.startTime}`
                                : DateTime.fromISO(getTeesheetDateTimeISO(teesheet), {
                                    zone: mapTimeZone(teesheet?.club),
                                  }).toFormat("h:mm a, EEE dd MMM yyyy")}
                            </p>
                          ))}
                      </>
                    ) : (
                      groupTeesheetsByClub(booking?.teesheets ?? []).map((group, groupIndex) => (
                        <div
                          key={group.club?._id ?? groupIndex}
                          className={
                            groupIndex > 0
                              ? "bookingListCard__clubGroup bookingListCard__clubGroup--bordered"
                              : "bookingListCard__clubGroup"
                          }
                        >
                          <h3 className="bookingListCard__clubName">
                            {capitalizeFullString(group.club?.name)}
                          </h3>
                          <p className="bookingListCard__location">
                            <em className="icon-location"></em>
                            {formatClubAddressLine(group.club)}
                          </p>
                          <label className="bookingListCard__fieldLabel">{t("Bookings.TeeTime")}</label>
                          {group.teesheets.map((teesheet: any, teeIndex: number) => (
                            <p key={teeIndex} className="bookingListCard__teeTime">
                              {teesheet?.isNetRate
                                ? `${DateTime.fromISO(getTeesheetDateTimeISO(teesheet), {
                                    zone: mapTimeZone(teesheet?.club),
                                  }).toFormat("dd MMM yyyy")}, ${teesheet.startTime}`
                                : DateTime.fromISO(getTeesheetDateTimeISO(teesheet), {
                                    zone: mapTimeZone(teesheet?.club),
                                  }).toFormat("h:mm a, EEE dd MMM yyyy")}
                            </p>
                          ))}
                        </div>
                      ))
                    )}
                    <div className="bookingListCard__golfers">
                      <span className="bookingListCard__fieldLabel">{t("Bookings.NumberOfGolfers")}</span>
                      <span className="bookingListCard__fieldValue">
                        {booking?.noOfGuest ?? booking?.teesheets?.[0]?.players} {t("Bookings.players")},{" "}
                        {booking?.teesheets?.[0]?.teesheetIdDetail?.holes ??
                          booking?.teesheets?.[0]?.teesheetObj?.holes ??
                          booking?.teesheets?.[0]?.course?.holes}{" "}
                        {t("Bookings.Holes")}
                      </span>
                    </div>
                    {booking?.orders?.products?.length > 0 && (
                      <div className="bookingListCard__addons">
                        {booking.orders.products.map((order: any, orderIndex: number) =>
                          order?.quantity ? (
                            <span key={orderIndex} className="badge">
                              {order.title}: {order.quantity}
                            </span>
                          ) : null
                        )}
                      </div>
                    )}
                  </div>

                  <div
                    className="bookingListCard__actionsCol"
                    onClick={stopCardNavigation}
                    onKeyDown={stopCardNavigation}
                    role="presentation"
                  >
                    <div className="bookingListCard__menuRow">
                      <div className="listActionDropdownBlock bookingListCard__menu">
                        <Dropdown align="end">
                          <Dropdown.Toggle className="toggleButton">
                            <em className="icon-vertical-elipse"></em>
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <NavLink
                              to={bookingDetailPath}
                              className="dropdown-item"
                            >
                              {t("Bookings.ViewDetail")}
                            </NavLink>
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                    </div>
                    <div className="bookingListCard__statusGroup">
                      {(booking?.status?.value === "cancelled" ||
                        booking?.status?.value === "refunded") && (
                        <span className="bookingListCard__statusText">
                          {booking?.status?.value === "cancelled"
                            ? t("Bookings.Cancelled")
                            : t("Bookings.Refunded")}
                        </span>
                      )}
                      {hasPendingTee && (
                        <StatusBadge
                          bg={"warning"}
                          text={t("Bookings.CourseConfirmationPending")}
                        />
                      )}
                      {booking?.teesheets?.find((tee: any) => tee?.isNetRate) && (
                        <StatusBadge
                          bg={"primary"}
                          text={t("NetRatePage.statusNetRate")}
                          textColor="white"
                        />
                      )}
                      {booking?.status?.value === "failed" && (
                        <span className="bookingListCard__statusText bookingListCard__statusText--failed">
                          {t("Bookings.Failed")}
                        </span>
                      )}
                    </div>
                    {booking?.status?.value === "completed" && (
                      <div className="bookingListCard__rating _rate">
                        <Rating
                          initialValue={booking?.rating ? booking.rating : 0}
                          showTooltip={false}
                          onClick={function noRefCheck() {}}
                          size={20}
                          fillColor={"#18C233"}
                          tooltipArray={["1.0", "2.0", "3.0", "4.0", "5.0"]}
                          readonly
                        />
                      </div>
                    )}
                  </div>
                </div>
              </article>
            );
          })}

        {showEmptyState && (
          <div className="noBookingBlock text-center mt-4 py-5">
            <img
              src="../assets/images/no-booking.png"
              alt="no-booking"
              className="img-fluid mx-auto d-block"
            />
            <h5 className="mt-4 pt-3 fontsize-16 fw600">
              {t("Bookings.message").replace("{{type}}", type)}
            </h5>
          </div>
        )}
      </div>
      )}
      {!isLoading && bookings && bookings.length > 0 && totalCount > 10 && (
        <div className="reactPaginateBlock mb-4">
          <Pagination totalCount={totalCount} setSkip={setSkip} pageCount={pageCount} />
        </div>
      )}
    </>
  );
};

export default BookingList;
